'use client';

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  ConfirmationDialog,
  HeadingSection,
  Scroll,
  Skeleton,
  UnverifiedAssetDetailHeader,
  UnverifiedAssetDetailsContent,
  UnverifiedAssetProbeSuccessDialog,
  UnverifiedAssetRerunContextDialog,
  toasterApi,
  useUnverifiedAssetProbeWatch,
  type UnverifiedAssetRerunContextValues,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { reportTelemetryError } from '@terra/ui-telemetry';
import type { UnverifiedEndpointMainContentTab } from '../../store/layoutStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useAppTargetId } from '../../store/projectStore';
import { terraProxyApiClient } from '../../clients/testerClient';
import { UNVERIFIED_ASSET_QUERY_KEY } from '../../hooks/api/useUnverifiedAsset';
import { ASSETS_SEARCH_QUERY_KEY } from '../../hooks/api/useAssetsSearch';

const PROBE_STATUS_QUERY_KEY = 'unverified-asset-probe-status';

export function UnverifiedEndpointDetailsTab({
  tab,
}: {
  tab: UnverifiedEndpointMainContentTab;
}) {
  const t = useTranslations('designSystem.applicationAssets');
  const appTargetId = useAppTargetId();
  const applicationId = tab.data.applicationId ?? appTargetId;
  const candidateId = tab.data.candidateId;
  const queryClient = useQueryClient();
  const { closeTab, openTab } = useMainContentTabs();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isContextDialogOpen, setIsContextDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRerunPending, setIsRerunPending] = useState(false);

  const {
    data: detail,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [UNVERIFIED_ASSET_QUERY_KEY, applicationId, candidateId],
    queryFn: async () => {
      if (!applicationId) {
        throw new Error('No application selected');
      }

      return terraProxyApiClient.getUnverifiedAsset({
        applicationId,
        candidateId,
      });
    },
    enabled: Boolean(applicationId && candidateId),
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const onProbeSettled = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [ASSETS_SEARCH_QUERY_KEY],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ['asset-counts'],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: [UNVERIFIED_ASSET_QUERY_KEY, applicationId, candidateId],
      }),
    ]);
  }, [applicationId, candidateId, queryClient]);

  const fetchProbeStatus = useCallback(async () => {
    if (!applicationId) {
      throw new Error('No application selected');
    }

    return terraProxyApiClient.getUnverifiedAssetProbeStatus({
      applicationId,
      candidateId,
    });
  }, [applicationId, candidateId]);

  const {
    isWatchingProbe,
    isSuccessDialogOpen,
    setIsSuccessDialogOpen,
    verifiedEndpointId,
    verifiedAssetDisplay,
    beginWatching,
  } = useUnverifiedAssetProbeWatch({
    probeStatusQueryKey: [PROBE_STATUS_QUERY_KEY, applicationId, candidateId],
    fetchProbeStatus,
    enabled: Boolean(applicationId && candidateId),
    method: detail?.request.method ?? 'GET',
    endpoint: detail?.url || detail?.path || '',
    onProbeSettled,
  });

  const startRerun = useCallback(
    async (context?: UnverifiedAssetRerunContextValues) => {
      if (!applicationId) {
        return;
      }

      setIsRerunPending(true);

      try {
        await terraProxyApiClient.rerunUnverifiedAsset({
          applicationId,
          candidateId,
          context,
        });
        toasterApi.success({ title: t('rerunStartedSuccess') });
        setIsContextDialogOpen(false);
        await queryClient.removeQueries({
          queryKey: [PROBE_STATUS_QUERY_KEY, applicationId, candidateId],
        });
        beginWatching();
      } catch (rerunError) {
        reportTelemetryError(rerunError, {
          operation: 'rerunUnverifiedAsset',
          applicationId,
          candidateId,
        });
        toasterApi.error({ title: t('rerunStartedError') });
      } finally {
        setIsRerunPending(false);
      }
    },
    [applicationId, beginWatching, candidateId, queryClient, t],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!applicationId) {
      return;
    }

    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteUnverifiedAsset({
        applicationId,
        candidateId,
      });
      toasterApi.success({ title: t('deleteUnverifiedAssetSuccess') });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [ASSETS_SEARCH_QUERY_KEY],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['asset-counts'],
          exact: false,
        }),
      ]);
      setIsDeleteDialogOpen(false);
      closeTab(tab.id);
    } catch (deleteError) {
      reportTelemetryError(deleteError, {
        operation: 'deleteUnverifiedAsset',
        applicationId,
        candidateId,
      });
      toasterApi.error({ title: t('deleteUnverifiedAssetError') });
    } finally {
      setIsDeleting(false);
    }
  }, [applicationId, candidateId, closeTab, queryClient, t, tab.id]);

  const handleViewVerifiedAsset = useCallback(() => {
    setIsSuccessDialogOpen(false);

    if (!verifiedEndpointId) {
      closeTab(tab.id);

      return;
    }

    const method = detail?.request.method ?? 'GET';
    const path = detail?.path ?? verifiedEndpointId;

    closeTab(tab.id);
    openTab({
      id: `endpoint-${verifiedEndpointId}`,
      title: `${method} ${path}`,
      type: 'endpoint',
      data: { endpointId: verifiedEndpointId },
    });
  }, [
    closeTab,
    detail,
    openTab,
    setIsSuccessDialogOpen,
    tab.id,
    verifiedEndpointId,
  ]);

  if (isLoading || (isWatchingProbe && !detail)) {
    return (
      <VStack gap="3" alignItems="stretch" p="4" h="full">
        <Skeleton height="72px" borderRadius="8px" />
        <Skeleton height="160px" borderRadius="8px" />
        <Skeleton height="240px" borderRadius="8px" />
      </VStack>
    );
  }

  if ((isError || !detail) && !isSuccessDialogOpen && !isWatchingProbe) {
    return (
      <VStack
        gap="2"
        alignItems="center"
        justifyContent="center"
        h="full"
        p="6"
        textAlign="center"
      >
        <HeadingSection color="fg.default">
          {isError
            ? t('unverifiedAssetLoadError')
            : t('unverifiedAssetNotFound')}
        </HeadingSection>
        {!isError ? (
          <BodyDefault color="fg.muted" maxW="md">
            {t('unverifiedAssetNotFoundDescription')}
          </BodyDefault>
        ) : null}
      </VStack>
    );
  }

  if (!detail) {
    return (
      <UnverifiedAssetProbeSuccessDialog
        open={isSuccessDialogOpen}
        onOpenChange={setIsSuccessDialogOpen}
        canViewVerifiedAsset={Boolean(verifiedEndpointId)}
        method={verifiedAssetDisplay?.method ?? 'GET'}
        endpoint={verifiedAssetDisplay?.endpoint ?? ''}
        onViewVerifiedAsset={handleViewVerifiedAsset}
      />
    );
  }

  const isRerunning = isRerunPending || isWatchingProbe;

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        <UnverifiedAssetDetailHeader
          detail={detail}
          isDeleting={isDeleting}
          isRerunning={isRerunning}
          onDelete={() => setIsDeleteDialogOpen(true)}
          onRerun={() => {
            void startRerun();
          }}
          onRerunWithContext={() => setIsContextDialogOpen(true)}
        />

        <Box flex="1" overflow="hidden">
          <Scroll orientation="vertical" showScrollbar h="full">
            <UnverifiedAssetDetailsContent detail={detail} variant="page" />
          </Scroll>
        </Box>
      </VStack>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => setIsDeleteDialogOpen(open)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title={t('deleteUnverifiedAssetTitle')}
        description={t('deleteUnverifiedAssetDescription')}
      />
      <UnverifiedAssetRerunContextDialog
        open={isContextDialogOpen}
        onOpenChange={setIsContextDialogOpen}
        isSubmitting={isRerunPending}
        method={detail.request.method}
        endpoint={detail.url || detail.path}
        onConfirm={startRerun}
      />
      <UnverifiedAssetProbeSuccessDialog
        open={isSuccessDialogOpen}
        onOpenChange={setIsSuccessDialogOpen}
        canViewVerifiedAsset={Boolean(verifiedEndpointId)}
        method={verifiedAssetDisplay?.method ?? detail.request.method}
        endpoint={verifiedAssetDisplay?.endpoint || detail.url || detail.path}
        onViewVerifiedAsset={handleViewVerifiedAsset}
      />
    </Box>
  );
}
